import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { api } from '../api/client';
import { useAuth } from '../stores/auth';
import { useFlags } from '../stores/flags';

// ── Tipos ──────────────────────────────────────────────────────
type Rubro = { id: number; nombre: string; icono: string };

type Comercio = {
  id: number;
  razon_social: string;
  nombre_fantasia: string | null;
  descripcion: string | null;
  direccion: string | null;
  latitud: string | null;
  longitud: string | null;
  telefono: string | null;
  whatsapp: string | null;
  logo_url: string | null;
  score_actual: number;
  tier_actual: number;
  rubro_id: number;
  rubro_nombre: string;
  rubro_icono: string;
  ofertas_activas_count: number;
  distancia_km: number | null;
};

// ── Helpers ────────────────────────────────────────────────────
function fmtDist(km: number | null) {
  if (km === null) return null;
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
}

// ── Componente principal ───────────────────────────────────────
export default function Comercios() {
  const nav        = useNavigate();
  const ciudadano  = useAuth((s) => s.ciudadano);
  const flagsData  = useFlags((s) => s.flags);
  const flagsAt    = useFlags((s) => s.fetchedAt);
  const hasFlag    = (f: string) => !flagsAt || flagsData[f] === true;

  const [rubros,    setRubros]    = useState<Rubro[]>([]);
  const [comercios, setComercios] = useState<Comercio[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);
  const [q,         setQ]         = useState('');
  const [rubroSel,  setRubroSel]  = useState(0);
  const [orden,     setOrden]     = useState<'score' | 'distancia' | 'nombre'>('score');
  const [geo,       setGeo]       = useState<{ lat: number; lng: number } | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Geolocalización ────────────────────────────────────────
  const pedirGeo = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const g = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setGeo(g);
        setOrden('distancia');
      },
      () => { /* silencioso si el usuario niega */ },
      { timeout: 8000 }
    );
  }, []);

  // ── Cargar rubros ──────────────────────────────────────────
  useEffect(() => {
    api<Rubro[]>('/comercios?action=rubros_publico', { auth: false })
      .then(setRubros)
      .catch(() => {});
  }, []);

  // ── Cargar comercios ───────────────────────────────────────
  const cargar = useCallback(
    async (opts?: { geoOverride?: typeof geo; rubroOverride?: number; ordenOverride?: typeof orden; qOverride?: string }) => {
      if (!ciudadano?.municipio_id) return;
      setError(null);
      const g = opts?.geoOverride !== undefined ? opts.geoOverride : geo;
      const r = opts?.rubroOverride !== undefined ? opts.rubroOverride : rubroSel;
      const o = opts?.ordenOverride !== undefined ? opts.ordenOverride : orden;
      const s = opts?.qOverride !== undefined ? opts.qOverride : q;

      const params = new URLSearchParams({ action: 'cercanos', limit: '200', orden: o });
      params.set('municipio_id', String(ciudadano.municipio_id));
      if (r) params.set('rubro_id', String(r));
      if (s) params.set('q', s);
      if (g) { params.set('lat', String(g.lat)); params.set('lng', String(g.lng)); }

      try {
        const d = await api<{ comercios: Comercio[] }>(`/comercios?${params}`);
        setComercios(d.comercios ?? []);
      } catch (e: any) {
        setError(e?.message ?? 'No se pudieron cargar los comercios');
        setComercios([]);
      } finally {
        setLoading(false);
      }
    },
    [ciudadano?.municipio_id, geo, rubroSel, orden, q]
  );

  // Carga inicial: geo → cargar
  useEffect(() => {
    pedirGeo();
    cargar();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recarga con debounce al cambiar filtros
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => cargar(), q ? 350 : 0);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [rubroSel, orden, q, geo, cargar]);

  const ordenLabels = { score: 'Mejor score', distancia: 'Cercanía', nombre: 'A-Z' };
  const ordenNext   = { score: 'distancia', distancia: 'nombre', nombre: 'score' } as const;

  const nombre = (c: Comercio) => c.nombre_fantasia || c.razon_social;

  return (
    <>
      <TopBar title="Comercios" back />

      <main className="max-w-md mx-auto pb-10">
        {/* Buscador */}
        <div className="px-4 pt-3 pb-2">
          <div
            className="flex items-center gap-2 rounded-xl px-3 py-2.5"
            style={{ backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0' }}
          >
            <span className="text-base leading-none">🔍</span>
            <input
              type="text"
              placeholder="Buscar comercio..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm text-gray-800 placeholder-gray-400"
            />
            {q && (
              <button onClick={() => setQ('')} className="text-gray-400 text-sm leading-none">✕</button>
            )}
          </div>
        </div>

        {/* Chips de rubro */}
        <div
          className="flex gap-2 overflow-x-auto px-4 pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' } as React.CSSProperties}
        >
          {[{ id: 0, nombre: 'Todos', icono: '🏪' }, ...rubros].map((r) => {
            const active = rubroSel === r.id;
            return (
              <button
                key={r.id}
                onClick={() => setRubroSel(r.id)}
                className="flex-shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold border-[1.5px] transition-colors"
                style={{
                  backgroundColor: active ? '#eff6ff' : '#f8fafc',
                  borderColor:     active ? '#3b82f6' : '#cbd5e1',
                  color:           active ? '#3b82f6' : '#64748b',
                }}
              >
                {r.icono} {r.nombre}
              </button>
            );
          })}
        </div>

        {/* Toolbar orden */}
        <div className="px-4 pb-3 flex justify-end">
          <button
            onClick={() => setOrden(ordenNext[orden])}
            className="flex-shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold border-[1.5px]"
            style={{ backgroundColor: '#f8fafc', borderColor: '#cbd5e1', color: '#64748b' }}
          >
            ↕️ {ordenLabels[orden]}
          </button>
        </div>

        {/* Estados */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
            <p className="text-sm text-gray-400">Cargando comercios...</p>
          </div>
        )}

        {!loading && error && (
          <div className="px-4 py-10 text-center">
            <p className="text-4xl mb-3">😕</p>
            <p className="text-sm text-gray-500">{error}</p>
            <button
              onClick={() => { setLoading(true); cargar(); }}
              className="mt-4 text-sm font-semibold text-blue-600"
            >
              Reintentar
            </button>
          </div>
        )}

        {!loading && !error && comercios.length === 0 && (
          <div className="px-4 py-16 text-center">
            <p className="text-5xl mb-3 opacity-50">🏪</p>
            <p className="font-bold text-gray-700 mb-1">
              {q || rubroSel ? 'Sin resultados' : 'Sin comercios todavía'}
            </p>
            <p className="text-sm text-gray-400">
              {q || rubroSel
                ? 'Probá ajustar los filtros o la búsqueda.'
                : 'Pronto se irán sumando comercios de tu municipio.'}
            </p>
          </div>
        )}

        {/* Lista */}
        {!loading && !error && comercios.length > 0 && (
          <div className="px-4 space-y-2.5">
            {comercios.map((c) => (
              <button
                key={c.id}
                onClick={() => nav(`/comercios/${c.id}`)}
                className="w-full flex items-center gap-3 rounded-2xl p-3 text-left"
                style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }}
              >
                {/* Logo / emoji */}
                <div
                  className="flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden flex items-center justify-center"
                  style={{ backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0' }}
                >
                  {c.logo_url ? (
                    <img
                      src={c.logo_url}
                      alt={nombre(c)}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <span className="text-4xl leading-none">{c.rubro_icono || '🏪'}</span>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-gray-900 truncate">{nombre(c)}</p>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    {c.rubro_icono} {c.rubro_nombre}
                  </p>
                  {c.direccion && (
                    <p className="text-xs text-gray-400 mt-0.5 truncate">{c.direccion}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-xs font-semibold" style={{ color: '#f59e0b' }}>
                      ⭐ {c.score_actual.toFixed(1)}
                    </span>
                    {c.distancia_km !== null && (
                      <span className="text-xs font-semibold text-blue-500">
                        📍 {fmtDist(c.distancia_km)}
                      </span>
                    )}
                    {hasFlag('mobile_comercios_ofertas') && c.ofertas_activas_count > 0 && (
                      <span className="text-xs font-semibold" style={{ color: '#10b981' }}>
                        🏷️ {c.ofertas_activas_count} oferta{c.ofertas_activas_count !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>

                <span className="text-xl font-bold text-gray-300 flex-shrink-0">›</span>
              </button>
            ))}
          </div>
        )}
      </main>
    </>
  );
}
